import { isWithinInterval, isBefore } from 'date-fns';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { AssetProps } from '@repo/ui/src/types/image';
import { ProductModelProps, ShopDataProps } from '@typings/product';
import { ProductProps } from '@repo/ui/src/types';
import { COLOR_STYLES } from '@components/Product/Details/_AlternateColors';
import { createProductVariation } from '@models/productVariation';

class Product implements ProductProps {
  listPosition;

  private data: ProductModelProps;
  private _quantity: number | null = null;
  private readonly addOn: { sku: string; label: string; overlayImage: AssetProps } | undefined;
  private _checked: boolean;
  // For updating shop data on the client
  private _shop: ShopDataProps | null = null;

  constructor(proData?: ProductModelProps | null, position?: number | null, checked = false) {
    this.data = proData ?? {};
    this.addOn = proData?.addOn;
    this.listPosition = position;
    this._checked = checked;
    if (proData?.shopData) {
      this.setShop(proData.shopData);
    }
  }

  get isAddon() {
    return !!this.addOn;
  }

  get monetate() {
    return this.data?.monetate;
  }

  get shop(): ShopDataProps {
    return this._shop ?? this.data?.shop ?? this.data?.shopData ?? ({} as ShopDataProps);
  }

  setShop(shop: ShopDataProps) {
    this._shop = shop;
  }

  get productData() {
    return this.data?.productData;
  }

  get overlayImage() {
    return this.addOn?.overlayImage;
  }

  get title() {
    return this?.data?.title ?? this?.monetate?.title ?? this.shop?.title ?? '';
  }

  get name() {
    return this.title ?? this.data?._meta?.name ?? '';
  }

  get smallHeading() {
    return this.data?.smallHeading;
  }

  get quantity() {
    if (this._quantity !== null) return this._quantity;
    if (this.isOrderable) return 1;
    if (this?.monetate) return 1;
    return this?.data?.quantity ?? 0;
  }

  set quantity(val: number) {
    this._quantity = val;
  }

  get sku() {
    return this?.productData?.sku ?? this?.monetate?.sku ?? this.shop?.id ?? '';
  }

  get parent() {
    return this.data?.parentProduct;
  }

  get product_id() {
    return this.sku;
  }

  get price() {
    return (
      (this.data?.giftCard?.amount ||
        this.shop?.price ||
        this.monetate?.price ||
        this.monetate?.originalPrice) ??
      0
    );
  }

  get affirmPrice() {
    return this.price * 100;
  }

  get originalPrice() {
    return this.shop?.originalPrice ?? this.monetate?.originalPrice ?? 0;
  }

  get discount() {
    return this.originalPrice - this.price;
  }

  get currency() {
    return this.data?.currency ?? this.shop?.currency ?? 'USD';
  }

  get images() {
    return this.data?.images ?? [];
  }

  get mainImage() {
    return this?.images && this.images.length > 0 ? this.images[0] : null;
  }

  get mainImageUrl() {
    return (
      this.shop?.mainImageUrl ||
      this.monetate?.mainImageUrl ||
      buildImageSrc(this.mainImage, 500, 1, 1)
    );
  }

  get image() {
    return this.mainImageUrl;
  }

  getMainImageSize(width: number, aspectWidth: number, aspectHeight: number) {
    const url = this.image.split('?')[0];
    return buildImageSrc(null, width, aspectWidth, aspectHeight, url);
  }

  get productUrl() {
    const url = this.shop?.productUrl ?? this.monetate?.link ?? '';

    return url.includes('.com') ? url.split('.com')[1] : url;
  }

  get deliveryId() {
    return this.data?._meta?.deliveryId;
  }

  get deliveryKey() {
    return this.data?._meta?.deliveryKey ?? this.productUrl;
  }

  get schema() {
    return this.data?._meta?.schema;
  }

  get inventory() {
    return this.shop?.inventory;
  }

  get shortTitle() {
    return this.data?.shortTitle;
  }
  get shortTitleMain() {
    const splitTitle = this.shortTitle?.trim().split(' ') ?? [];
    return splitTitle.length <= 1
      ? this.shortTitle
      : splitTitle.slice(0, splitTitle.length - 1).join(' ');
  }

  get shortTitlePrimary() {
    const splitTitle = this.shortTitle?.trim().split(' ') ?? [];
    return splitTitle.length > 1 ? splitTitle[splitTitle.length - 1] : '';
  }

  get shortDescription() {
    return this.shop?.description ?? this.data?.shortDescription ?? this.data?.boldDescription;
  }

  get description() {
    return (
      this.data?.description ??
      this.data?.shortDescription ??
      this.data?.boldDescription ??
      this.monetate?.description ??
      ''
    );
  }

  get categories() {
    return this.productData?.categories ?? [];
  }

  get mainCategory() {
    return this.categories?.[0]?.categories ?? this.monetate?.category ?? '';
  }

  get category() {
    return this.mainCategory;
  }

  get newArrivalStart() {
    return this.productData?.newArrivalStart;
  }

  get newArrivalEnd() {
    return this.productData?.newArrivalEnd;
  }

  get color(): keyof typeof COLOR_STYLES {
    return (this?.data?.color ?? this.shop?.color ?? '') as any;
  }

  get ctaButtonType() {
    return this.shop?.ctaButtonType;
  }

  get frontFacingImages() {
    return this.data?.frontFacingImages;
  }

  get transparentImages() {
    return this.data?.transparentImages;
  }

  get attributes() {
    return { ...this };
  }

  get isOrderable() {
    if (this.monetate?.recToken) return true; // The api will ensure items that are provided are also in stock
    return this?.inventory?.orderable && this.price > 0;
  }

  get promotionId() {
    return this?.shop?.promotionIds?.split(',') ?? undefined;
  }

  get promotions() {
    return this?.shop?.promotions;
  }

  get hasDiscount() {
    return this.discount > 0;
  }

  get isNewProduct() {
    return !!(
      this?.newArrivalStart &&
      this?.newArrivalEnd &&
      isWithinInterval(new Date(), {
        start: new Date(this.newArrivalStart),
        end: new Date(this.newArrivalEnd),
      })
    );
  }

  get isComingSoon() {
    return !!(this?.newArrivalStart && isBefore(new Date(), new Date(this.newArrivalStart)));
  }

  get weight() {
    return this.shop?.weight;
  }

  get ratingSummary() {
    return this.data?.ratingSummary ?? this.monetate?.ratingSummary;
  }

  get compatibleWith() {
    return this.data?.compatibleWith;
  }

  set checked(val: boolean) {
    this._checked = val;
  }

  get checked() {
    return this._checked;
  }

  get customFields() {
    return this.shop.customFields ? JSON.parse(this.shop.customFields) : {};
  }

  get variationValues() {
    return this.shop?.variationValues;
  }
  get variationAttributes() {
    return this.shop?.variationAttributes;
  }
  get variations() {
    return this.shop?.variations?.map(createProductVariation) ?? [];
  }
}

export default Product;
